import { useMutation, useQuery } from "@tanstack/react-query"
import { EProgramBooking, EProgramBookingTicket, getAllProgramBookingAdminByProgramId, updateProgramBookingStatusAdminByUpdateProgramBookingStatusRequest } from "../../../../../service/programBooking"
import { ColumnsType } from "antd/es/table"
import dayjs from "dayjs"
import { EProgram, getProgramById } from "../../../../../service/program"
import { ProgramBookingDetailModal } from "../modal/update"
import { Tag } from "antd"

enum EProgramBookingStatus {
    PENDING = 'PENDING',
    REQUEST_PAID = 'REQUEST_PAID',
    COMPLETE = 'COMPLETE',
    REQUEST_CANCEL = 'REQUEST_CANCEL',
    CANCEL = 'CANCEL',
    REFUND = 'REFUND',
    CHECK_IN = 'CHECK_IN',
    ABSENT = 'ABSENT',
}

const defaultProgramBookingFieldListMap: IProgramBookingField = {
    programBooking: [EProgramBooking.id, EProgramBooking.memberName, EProgramBooking.memberMobile, EProgramBooking.status, EProgramBooking.paidAmount, EProgramBooking.targetDateTime, EProgramBooking.bookingDateTime, EProgramBooking.programEventId, EProgramBooking.tossPayId, EProgramBooking.paidDateTime, EProgramBooking.programBookingTicketList],
    [EProgramBooking.programBookingTicketList]: [EProgramBookingTicket.name, EProgramBookingTicket.count]
}

const useGetAllProgramBookingAdminByProgramId = (programId: number, programBookingField: IProgramBookingField = defaultProgramBookingFieldListMap) => {
    const query = useQuery({
        queryKey: ['getAllProgramBookingAdminByProgramId', programId],
        queryFn: () => getAllProgramBookingAdminByProgramId<IProgramApplyForList[]>(programId, programBookingField),
        enabled: !!programId,
        select: res => res.data.data.getAllProgramBookingAdminByProgramId.sort((a, b) => b.id - a.id),
    })

    return query;
}

const defaultProgramFieldListMap: IProgramField = {
    program: [EProgram.id, EProgram.title]
}

const useGetProgramTitleById = (id: number, programField: IProgramField = defaultProgramFieldListMap) => {
    const query = useQuery({
        queryKey: ['getProgramById', id],
        queryFn: () => getProgramById(id, programField),
        enabled: !!id,
        select: res => res.data.data.getProgramById,
    });

    return query;
}

const useUpdateProgramBookingStatusAdminByUpdateProgramBookingStatusRequest = () => {
    const mutation = useMutation({
        mutationFn: updateProgramBookingStatusAdminByUpdateProgramBookingStatusRequest,
    })

    return mutation;
}

const statusTagColor = (q: EProgramBookingStatus) => {
    switch (q) {
        case EProgramBookingStatus.PENDING:
            return 'gold';
        case EProgramBookingStatus.REQUEST_PAID:
            return 'magenta';
        case EProgramBookingStatus.COMPLETE:
            return 'green';
        case EProgramBookingStatus.REQUEST_CANCEL:
            return 'magenta';
        case EProgramBookingStatus.CANCEL:
            return;
        case EProgramBookingStatus.REFUND:
            return;
        case EProgramBookingStatus.CHECK_IN:
            return 'green';
        case EProgramBookingStatus.ABSENT:
            return 'magenta';
        default:
            return 'magenta';
    }
}


const columns: ColumnsType<IProgramApplyForList> = [
    {
        title: 'No.',
        dataIndex: 'id',
        sorter: (a, b) => a.id - b.id
    },
    {
        title: '이름',
        dataIndex: 'memberName',
        render: (q: string, record) => <ProgramBookingDetailModal btnText={q} selectProgramBookingId={record.id} />
    },
    {
        title: '티켓/인원',
        dataIndex: 'programBookingTicketList',
        render: (q: IProgramBookingTicketAndTicket[]) => q.map((w, idx) => w.count > 0 && <div key={idx}>{w.name + " X " + w.count}</div>)
    },
    {
        title: '전화번호',
        dataIndex: 'memberMobile',
    },
    {
        title: '신청일시',
        dataIndex: 'bookingDateTime',
        render: q => dayjs(q).format('YYYY.MM.DD HH:mm')
    },
    {
        title: '프로그램 이용일시',
        dataIndex: 'targetDateTime',
        render: q => <div>{dayjs(q).format('YYYY.MM.DD HH:mm')}</div>,
        sorter: (a, b) => dayjs(a.targetDateTime).diff(dayjs(b.targetDateTime))
    },
    {
        title: '상태',
        dataIndex: 'status',
        render: (q: EProgramBookingStatus) => <Tag color={statusTagColor(q)}>{q}</Tag>
    },
    {
        title: '결제금액',
        dataIndex: 'paidAmount',
        render: (q, record) => textToProgramBookingStatusMap[record.status] === EProgramBookingStatus.PENDING ? 0 : q?.toLocaleString()
    },
    {
        title: '결제일시',
        dataIndex: 'paidDateTime',
        render: q => q ? dayjs(q).format('YYYY.MM.DD HH:mm') : '-'
    },
]
const textToProgramBookingStatusMap: { [key: string]: EProgramBookingStatus } = {
    "예약 대기": EProgramBookingStatus.PENDING,
    "결제 요청": EProgramBookingStatus.REQUEST_PAID,
    "예약 확정": EProgramBookingStatus.COMPLETE,
    "취소 요청": EProgramBookingStatus.REQUEST_CANCEL,
    "취소 완료": EProgramBookingStatus.CANCEL,
    "환불 완료": EProgramBookingStatus.REFUND,
    "참석": EProgramBookingStatus.CHECK_IN,
    "불참": EProgramBookingStatus.ABSENT,
};

const programBookingStatusToTextMap: { [key in EProgramBookingStatus]: string } = {
    [EProgramBookingStatus.PENDING]: "예약 대기",
    [EProgramBookingStatus.REQUEST_PAID]: "결제 요청",
    [EProgramBookingStatus.COMPLETE]: "예약 확정",
    [EProgramBookingStatus.REQUEST_CANCEL]: "취소 요청",
    [EProgramBookingStatus.CANCEL]: "취소 완료",
    [EProgramBookingStatus.REFUND]: "환불 완료",
    [EProgramBookingStatus.CHECK_IN]: "참석",
    [EProgramBookingStatus.ABSENT]: "불참",
};

export { useGetProgramTitleById, useGetAllProgramBookingAdminByProgramId, useUpdateProgramBookingStatusAdminByUpdateProgramBookingStatusRequest, columns, EProgramBookingStatus, textToProgramBookingStatusMap, programBookingStatusToTextMap, statusTagColor }